import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MTopBar, SaleBar } from "@/components/mobile/MTopBar";
import { MPage, EmptyArt, Loading, cardCls, blueBtn } from "@/components/mobile/parts";
import { AddItems } from "@/components/mobile/AddItems";
import { useCartActions } from "@/pages/mobile/MProducts";
import { useMobileCatalog } from "@/hooks/useMobileData";
import { useAuth } from "@/context/AuthContext";
import { api, canSee, errMsg, isManagerRole } from "@/lib/api";
import { cn } from "@/lib/utils";

export const DIRECT_CART = "direct";

function OpenCash({ me, onOpened }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      await api.post("/cash/open", { opening_amount: Number(v.replace(",", ".")) || 0, user_id: me.id }, { params: { mode: "ticket" } });
      toast.success("Caixa aberto");
      onOpened();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-direct-open-cash">
      <label className="block space-y-1.5 text-sm"><span className="font-semibold">Valor inicial em dinheiro (troco)</span>
        <Input value={v} onChange={(e) => setV(e.target.value)} inputMode="decimal" placeholder="0,00" className="h-12 bg-white text-base" data-testid="m-direct-opening-input" /></label>
      <Button className={cn("h-12 w-full", blueBtn)} onClick={open} disabled={busy} data-testid="m-direct-open-cash-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Abrir meu caixa</Button>
    </div>
  );
}

function Blocked({ title, text, children, testId }) {
  return <MPage><EmptyArt icon={Lock} title={title} text={text} testId={testId} />{children}</MPage>;
}

export default function MDirectSale() {
  const { session } = useAuth();
  const nav = useNavigate();
  const u = session.user;
  const allowed = canSee(u, "venda_ticket");
  const [cash, setCash] = useState(null);
  const { products, categories, ready } = useMobileCatalog();
  const { cart, add, total, count } = useCartActions(DIRECT_CART);
  const loadCash = useCallback(() => api.get("/cash/current", { params: { mode: "ticket" } }).then((r) => setCash(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { if (allowed) loadCash(); }, [allowed, loadCash]);
  const sale = useMemo(() => products.filter((p) => p.ticket_sale), [products]);
  const cats = useMemo(() => categories.filter((c) => sale.some((p) => p.category_id === c.id)), [categories, sale]);

  let body;
  if (!allowed) body = <Blocked title="Seu usuário não faz Venda ticket" text="Peça ao gerente para liberar a Venda ticket no seu usuário." testId="m-direct-not-allowed" />;
  else if (!cash) body = <Loading />;
  else if (!cash.open) {
    body = (
      <Blocked title="Seu caixa está fechado" text={isManagerRole(u) ? "Abra o seu caixa para começar a vender." : "Peça ao gerente ou administrador para abrir o seu caixa."} testId="m-direct-cash-closed">
        {isManagerRole(u) && <OpenCash me={u} onOpened={loadCash} />}
      </Blocked>
    );
  } else if (ready && !sale.length) {
    body = <MPage><EmptyArt title="Nenhum produto liberado" text="No sistema, em Produtos, ative a opção 'Na Venda ticket' nos itens que podem ser vendidos aqui." testId="m-direct-no-products" /></MPage>;
  } else {
    body = <AddItems products={sale} categories={cats} ready={ready} cart={cart} add={add} total={total} count={count} onCart={() => nav("/m/venda-direta/carrinho")} />;
  }
  return (
    <>
      <MTopBar title="Adicionar itens" back="/m/pdv" />
      <SaleBar />
      {body}
    </>
  );
}
